<template>
  <Layout
    :errors="errors"
    :title="'Dashboard: ' + trans(`models.${resourceType}.name.plural`)"
    :toasts="toasts"
    :resource="model"
  >
    <div class="model-title-bar">
      <div class="flex row pe-2">
        <div class="col flex-grow-1">
          <h2 class="fw-bold text-uppercase p-1">Dashboard: {{ trans(`models.${resourceType}.name.plural`) }}</h2>
        </div>
        <resource-action-buttons
          :button-class="'btn btn-primary text-secondary'"
          :model="model"
          :resource-type="resourceType"
          :source-action="'dashboard'"
        ></resource-action-buttons>
      </div>
      <div v-if="hasDescription(trans)" class="card rounded-corner">
        <div class="card-body">
          <p class="card-text">
            {{ trans(`models.${resourceType}.content.index.description`) }}
          </p>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-8">
        <ul class="nav nav-tabs gap-1" style="margin: 0 0 -1px 0.5rem">
          <li class="nav-item">
            <button
              :class="focus === 'self' ? 'active' : 'border border-bottom-0 text-muted bg-light'"
              class="nav-link"
              @click="setFocus('self')"
            >
              {{ trans(`models.${resourceType}.name.singular`) }}
            </button>
          </li>
          <template v-if="model.data.id">
            <template v-for="(collection, name) in model.data.collections" :key="name">
              <li v-if="meta.editable.includes(name)" class="nav-item">
                <button
                  :class="focus === name ? 'active' : 'border border-bottom-0 text-muted bg-light'"
                  class="nav-link"
                  @click="setFocus(name)"
                >
                  {{ trans(`models.${resourceType}.relations.${name}`) }}
                </button>
              </li>
            </template>
          </template>
        </ul>
        <div v-if="model.data && focus === 'self'" class="card rounded-corner">
          <div class="card-body position-relative">
            <div class="model-title-bar">
              <h5 class="card-title text-primary fw-bold text-uppercase">
                {{ trans(`models.${resourceType}.content.` + (model.data.id ? 'edit' : 'create') + `.action`) }}
              </h5>
              <p class="card-text">
                {{ trans(`models.${resourceType}.content.` + (model.data.id ? 'edit' : 'create') + `.description`) }}
              </p>
            </div>
            <resource-form :key="model.data.id" :errors="errors" :resource="model"></resource-form>
          </div>
        </div>
        <div v-if="model.data.id">
          <div v-for="(collection, name) in model.data.collections" :key="name">
            <template v-if="meta.editable.includes(name) && focus == name">
              <relation-collection-card
                :name="name"
                :resource="model"
                @updatePicked="(picked) => updatePicked(picked, name)"
                @detach="(detached) => detach(name, detached)"
                @trigger:refresh="reload()"
              ></relation-collection-card>
              <div v-if="model.meta.belongsToMany[name]" class="row">
                <div class="col-12">
                  <card
                    :id="`resource-component_${resourceType}-${name}_selection`"
                    :title="trans('models.' + resourceType + '.relations.' + name) + ' hinzufügen'"
                    class="mb-2"
                  >
                    <resource-selection
                      :name="name"
                      :resource-type="model.meta.collections[name]"
                      :origin="model"
                      @update:attached="(attached) => attach(name, attached)"
                    ></resource-selection>
                  </card>
                </div>
              </div>
              <div v-else-if="model.meta.hasMany[name]" class="row">
                <div class="col-12">
                  <picked-resource-card :name="name" :picked="picked[name]" :resource="model"></picked-resource-card>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div class="col-4">
        <div class="card rounded-corner">
          <div class="card-body position-relative">
            <resource-collection
              :collection-type="model.meta.config.component?.type ?? 'list'"
              :meta="model.meta"
              :mode="'dashboard'"
              :models="models"
              :pagination="false"
              @add-toast="addToast"
            ></resource-collection>
          </div>
        </div>
      </div>
    </div>
    <div v-if="models.page" class="admin-footer mt-3">
      <pagination :name="trans(`models.${resourceType}.name.plural`)" :resource="models"></pagination>
    </div>
  </Layout>
</template>

<script>
import Card from '@/structures/default/components/Base/Card';
import Layout from '@/structures/moviary/Pages/Layout';
import ResourceForm from '@/structures/default/components/ResourceForm';
import Pagination from '@/structures/default/components/Pagination';
import ResourceActionButtons from '@/structures/default/components/Links/ResourceActionButtons';
import ResourceCollection from '@/structures/default/components/ResourceCollection';
import ResourceSelection from '@/structures/default/components/ResourceSelection';
import useCustomActionBox from '@/structures/default/composables/useCustomActionBox';
import useResourceParams from '@/structures/default/composables/useResourceParams';
import useToasts from '@/structures/default/composables/useToasts';
import useResource from '@/structures/default/composables/useResource';
import useModels from '@/structures/default/composables/useModels';
import { ref } from 'vue';
import { router } from '@inertiajs/vue3';
import PickedResourceCard from '@/structures/default/components/Cards/PickedResource';
import RelationCollectionCard from '@/structures/default/components/Cards/RelationCollection';

export default {
  components: {
    Card,
    Layout,
    ResourceForm,
    Pagination,
    PickedResourceCard,
    RelationCollectionCard,
    ResourceActionButtons,
    ResourceCollection,
    ResourceSelection,
  },
  props: {
    aspectMenuItems: { type: Object, default: () => {} },
    model: { type: Object, default: () => {} },
    models: { type: Object, default: () => {} },
    toasts: { type: Object, default: () => {} },
    errors: { type: Object, default: () => {} },
    config: { type: Object, default: () => {} },
    customActionData: { type: Object, default: () => {} },
  },
  setup(props) {
    const { meta, resourceType } = useResource(props, props.model);
    const picked = ref(props.model.data.picked);
    const { customActionBox } = useCustomActionBox(props);
    const { addToast } = useToasts(props);
    const { getIndexParams } = useResourceParams(props);
    const focus = ref(props.model.meta.adjustments.focus ?? 'self');

    const hasDescription = (trans) => {
      let transString = `models.${props.resourceType}.content.index.description`;
      let translation = trans(transString);

      return translation && translation !== transString;
    };

    const updatePicked = (pickedModel, name) => {
      picked.value[name].data = pickedModel.model.data;
    };

    const setFocus = (name) => {
      const { focus: setFocusAdjustment } = useModels({ resourceType: props.model.meta.resourceType });
      setFocusAdjustment(props.model.meta.resourceType, name);
      focus.value = name;
    };

    const attach = async (name, attached) => {
      const { attachModel } = useModels({ resourceType: props.model.meta.resourceType });
      await attachModel(props.model.data.id, props.model.meta.resourceType, name, attached.data.id);
      router.reload();
    };

    const detach = async (name, detached) => {
      const { detachModel } = useModels({ resourceType: props.model.meta.resourceType });
      await detachModel(props.model.data.id, props.model.meta.resourceType, name, detached.data.id);
      router.reload();
    };

    const reload = () => {
      router.reload();
    };

    return {
      addToast,
      attach,
      customActionBox,
      detach,
      focus,
      getIndexParams,
      hasDescription,
      meta,
      picked,
      reload,
      resourceType,
      setFocus,
      updatePicked,
    };
  },
};
</script>
